import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
export function ChildSystemOutput() {
  const {
    reportId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const state = searchParams.get('state') || 'preview';
  const isUnlocked = state === 'full';
  const childData = {
    name: 'Aarav',
    dateOfBirth: '4 May 2018',
    timeOfBirth: '10:12 AM',
    placeOfBirth: 'Pune, India'
  };
  const previewContent = {
    section1: {
      title: '1. Core Temperament & Nervous System Type',
      subtitle: 'High-sensitivity, depth-processing system.',
      content: `This child's system:

• processes stimulation deeply
• is emotionally receptive to the environment
• gets overwhelmed by excess noise, pressure, or chaos
• needs more recovery time than average

This is not fragility. This is high-resolution processing.`
    },
    section2: {
      title: '2. Stress & Safety Pattern',
      content: `When the system feels unsafe or overloaded:

• it withdraws
• becomes quiet or resistant
• may appear "uncooperative" when actually overloaded

When it feels safe:

• it becomes playful
• expressive
• curious
• connected`
    }
  };
  const fullReportContent = {
    section3: {
      title: '3. Learning & Energy Style',
      content: `This system learns best through:

• depth and interest, not repetition
• calm environments
• one-on-one or small-group settings

Energy comes in waves, not constant output.`
    },
    section4: {
      title: '4. Emotional Regulation Pattern',
      content: `• Needs external calm to find internal calm
• Takes longer to recover from emotional spikes
• Does not respond well to pressure-based regulation`
    },
    section5: {
      title: '5. Environment Fit & Overload Risks',
      content: `Overload signals:

• withdrawal
• irritability
• shutdown

Thrives in:

• predictable environments
• emotionally stable fields
• low-chaos settings`
    },
    section6: {
      title: '6. Attachment & Trust Signals',
      content: `Trust is built through:

• consistency
• tone of voice
• emotional presence

Not through:

• logic
• pressure
• correction-first approaches`
    },
    section7: {
      title: '7. Parenting Mismatch Risks',
      content: `This system is often misunderstood as:

• "too sensitive"
• "too slow"
• "too emotional"

Which creates unnecessary pressure loops.`
    },
    section8: {
      title: '8. Structural Strengths & Sensitivities',
      content: `Strengths:

• empathy
• depth
• creativity
• emotional intelligence

Sensitivities:

• overstimulation
• emotional chaos
• rushed environments`
    },
    section9: {
      title: '9. Developmental Pressure Points',
      content: `Key risk zones:

• school transitions
• performance pressure
• emotionally chaotic authority figures`
    },
    section10: {
      title: '10. Summary Map',
      content: `This is a high-sensitivity, depth-processing child system that thrives in calm, predictable, emotionally coherent environments.

The goal is not to change this system. It is to stop misreading it.`
    }
  };
  const handleUnlock = () => {
    navigate(`/report/child/${reportId}/unlock`);
  };
  const formatContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('•')) {
        return <div key={i} className="flex items-start gap-3 ml-4" data-id="element-521">
            <span className="text-[#64748B] mt-1.5" data-id="element-522">•</span>
            <span data-id="element-523">{line.substring(1).trim()}</span>
          </div>;
      }
      if (line.trim().startsWith('"') && line.trim().endsWith('"')) {
        return <blockquote key={i} className="border-l-2 border-[#64748B] pl-6 italic text-[#F5F5F5]/90 my-4" data-id="element-524">
            {line.trim().slice(1, -1)}
          </blockquote>;
      }
      if (line.trim() === '') {
        return <div key={i} className="h-4" data-id="element-525" />;
      }
      return <p key={i} data-id="element-526">{line}</p>;
    });
  };
  return <>
      <Helmet data-id="element-527">
        <title data-id="element-528">Child System Map — Report | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-529" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-530">
        <header data-id="element-531">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-532">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-533">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-534">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" data-id="element-535" />
                <span className="text-lg font-medium tracking-tight" data-id="element-536">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-537">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-538">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-539">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-24 px-6" data-id="element-540">
          <div className="max-w-4xl mx-auto" data-id="element-541">
            <section className="mb-16" data-id="element-542">
              <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-543">
                <div className="flex items-start justify-between mb-6" data-id="element-544">
                  <h1 className="text-3xl md:text-4xl font-medium leading-tight tracking-tight" data-id="element-545">
                    🧒 Child System Map
                  </h1>
                  <div className={`px-3 py-1 border rounded text-xs uppercase tracking-wider flex items-center gap-2 ${isUnlocked ? 'bg-[#64748B]/20 border-[#64748B]/30 text-[#64748B]' : 'bg-[#2B2B2B]/20 border-[#2B2B2B] text-[#F5F5F5]/50'}`} data-id="element-546">
                    <span data-id="element-547">{isUnlocked ? '🟢' : '🟡'}</span>
                    {isUnlocked ? 'Full Map' : 'Preview'}
                  </div>
                </div>
                <div className="space-y-2 text-sm text-[#F5F5F5]/60 mb-6" data-id="element-548">
                  <p data-id="element-549">
                    <strong className="text-[#F5F5F5]/80" data-id="element-550">Child's Name:</strong>{' '}
                    {childData.name}
                  </p>
                  <p data-id="element-551">
                    <strong className="text-[#F5F5F5]/80" data-id="element-552">
                      Date of Birth:
                    </strong>{' '}
                    {childData.dateOfBirth}
                  </p>
                  <p data-id="element-553">
                    <strong className="text-[#F5F5F5]/80" data-id="element-554">Time:</strong>{' '}
                    {childData.timeOfBirth}
                  </p>
                  <p data-id="element-555">
                    <strong className="text-[#F5F5F5]/80" data-id="element-556">Place:</strong>{' '}
                    {childData.placeOfBirth}
                  </p>
                </div>
                <blockquote className="border-l-2 border-[#64748B] pl-6 italic text-[#F5F5F5]/80" data-id="element-557">
                  This is a structural map of a child's nervous system and
                  natural operating style. This is not a diagnosis.
                </blockquote>
              </div>
            </section>

            <section className="mb-16" data-id="element-558">
              {!isUnlocked && <div className="mb-8 flex items-center gap-3" data-id="element-559">
                  <div className="px-3 py-1 bg-[#64748B]/20 border border-[#64748B]/30 rounded text-xs text-[#64748B] uppercase tracking-wider" data-id="element-560">
                    Preview Layer
                  </div>
                </div>}

              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-561">
                <h2 className="text-2xl font-medium mb-3 tracking-tight" data-id="element-562">
                  {previewContent.section1.title}
                </h2>
                <h3 className="text-lg font-medium mb-6 text-[#F5F5F5]/80" data-id="element-563">
                  {previewContent.section1.subtitle}
                </h3>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-564">
                  {formatContent(previewContent.section1.content)}
                </div>
              </div>

              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-565">
                <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-566">
                  {previewContent.section2.title}
                </h2>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-567">
                  {formatContent(previewContent.section2.content)}
                </div>
              </div>

              {!isUnlocked && <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-568">
                  <p className="text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-569">
                    🔒 This is the first layer of your Child System Map.
                  </p>
                  <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-570">
                    The full map continues with:
                  </p>
                  <ul className="space-y-2 text-[#F5F5F5]/60 text-sm mb-6" data-id="element-571">
                    <li data-id="element-572">• Learning & energy style</li>
                    <li data-id="element-573">• Emotional regulation pattern</li>
                    <li data-id="element-574">• Environment fit & overload risks</li>
                    <li data-id="element-575">• Attachment & trust signals</li>
                    <li data-id="element-576">• Parenting mismatch risks</li>
                    <li data-id="element-577">• Structural strengths & sensitivities</li>
                    <li data-id="element-578">• Developmental pressure points</li>
                  </ul>
                </div>}
            </section>

            {!isUnlocked && <section className="mb-16" data-id="element-579">
                <div className="border-2 border-[#64748B]/30 p-12 bg-[#111214] text-center" data-id="element-580">
                  <Lock className="w-12 h-12 text-[#64748B] mx-auto mb-6" data-id="element-581" />
                  <h2 className="text-3xl font-medium mb-6" data-id="element-582">Unlock Full Map</h2>
                  <p className="text-lg text-[#F5F5F5]/70 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-583">
                    This is not a different report. This is the rest of the same
                    map.
                  </p>

                  <div className="mb-12" data-id="element-584">
                    <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-6 uppercase tracking-wider" data-id="element-585">
                      Locked Sections
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto text-left" data-id="element-586">
                      {['Learning & Energy Style', 'Emotional Regulation Pattern', 'Environment Fit & Overload Risks', 'Attachment & Trust Signals', 'Parenting Mismatch Risks', 'Structural Strengths & Sensitivities', 'Developmental Pressure Points', 'Summary Map'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-587">
                          <Lock className="w-4 h-4 text-[#64748B] flex-shrink-0 mt-0.5" data-id="element-588" />
                          <span className="text-[#F5F5F5]/70 text-sm" data-id="element-589">
                            {item}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  <button onClick={handleUnlock} className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-590">
                    🔓 Unlock Full Map →
                  </button>

                  <p className="mt-6 text-xs text-[#F5F5F5]/40" data-id="element-591">
                    One-time unlock · Complete map access
                  </p>
                </div>
              </section>}

            {isUnlocked && <section id="unlocked-content" data-id="element-592">
                <div className="mb-8 flex items-center gap-3" data-id="element-593">
                  <div className="px-3 py-1 bg-[#64748B]/20 border border-[#64748B]/30 rounded text-xs text-[#64748B] uppercase tracking-wider" data-id="element-594">
                    Full Map
                  </div>
                </div>

                {Object.values(fullReportContent).map((section, i) => <div key={i} className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-595">
                    <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-596">
                      {section.title}
                    </h2>
                    <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-597">
                      {formatContent(section.content)}
                    </div>
                  </div>)}

                <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-598">
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-599">
                    This map explains how a child's system operates. It does not
                    tell you what to do, how to change them, or who they should
                    become. This is a diagnostic document, not a prescription.
                  </p>
                </div>
              </section>}
          </div>
        </main>

        <footer className="py-8 px-6 border-t border-[#2B2B2B]" data-id="element-600">
          <div className="max-w-7xl mx-auto text-center" data-id="element-601">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-602">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}